import { createApi } from "@reduxjs/toolkit/query/react";

import { REQUEST_METHODS } from "@/constants/global-constants";
import {
  ChatGroup,
  ChatType,
  Group,
  IChatList,
  InitialMessage,
  TChatMedia,
  User,
} from "@/interfaces/message.interface";
import {
  TCreateChatgroup,
  TUpdateChatgroup,
  TUpdateMessage,
} from "@/schemas/message.schema";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export interface ExtendedInitalMessage<T> extends GeneralSingleResponse<T> {
  totalResult: number;
}

export const messageApi = createApi({
  reducerPath: "messageApi",
  baseQuery: BaseWithAuth(),
  tagTypes: [
    "user-chat",
    "group",
    "socket-change",
    "single-group",
    "single-user",
  ],
  endpoints: (builder) => ({
    // User

    // getInfinitePokemonWithMax: builder.infiniteQuery<
    //   InitialMessage[],
    //   string,
    //   { offset: number; limit: number; filter?: string }
    // >({
    //   infiniteQueryOptions: {
    //     // Must provide a default initial page param value
    //     initialPageParam: { offset: 0, limit: 20, filter: "" },

    //     // Must provide a `getNextPageParam` function
    //     getNextPageParam: (lastPage, allPages, lastPageParam) =>
    //       const nextOffs
    //     // Optionally provide a `getPreviousPageParam` function
    //     getPreviousPageParam: (
    //       firstPage,
    //       allPages,
    //       firstPageParam,
    //       allPageParams,
    //     ) => {
    //       return firstPageParam > 0 ? firstPageParam - 1 : undefined;
    //     },
    //   },
    //   // The `query` function receives `{queryArg, pageParam}` as its argument
    //   query({ queryArg, pageParam }) {
    //     return `/type/${queryArg}?page=${pageParam}`;
    //   },
    // }),

    getUserMessage: builder.query<
      ExtendedInitalMessage<InitialMessage[]>,
      { receiverId: string; filter?: string }
    >({
      query: ({ receiverId, filter }) => {
        return `/workspace/${getWorkspace()}/chat/user/${receiverId}/message/${filter ?? ""}`;
      },
      providesTags: ["socket-change"],
    }),

    getUserMsgDetail: builder.query<TChatMedia, { userId: string }>({
      query: ({ userId }) => {
        return `/workspace/${getWorkspace()}/chat/user/${userId}`;
      },
      providesTags: ["socket-change", "single-user"],
      transformResponse: (response: APIResponse<TChatMedia>) => response?.data,
    }),

    sendUserMsg: builder.mutation<
      object,
      { receiverId: string; data: FormData }
    >({
      query: ({ receiverId, data }) => ({
        url: `/workspace/${getWorkspace()}/chat/user/${receiverId}/message`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["user-chat", "single-group", "single-user"],
    }),
    mutateUserMsg: builder.mutation<
      object,
      { messageId: string; data?: TUpdateMessage; method: "PUT" | "DELETE" }
    >({
      query: ({ messageId, data, method }) => ({
        url: `/workspace/${getWorkspace()}/chat/message/${messageId}`,
        method,
        body: data,
      }),
      invalidatesTags: ["user-chat", "single-group", "single-user"],
    }),

    // Group chat
    getGroups: builder.query<ChatGroup[], string>({
      query: () => {
        return `/workspace/${getWorkspace()}/chat/group`;
      },
      providesTags: ["group"],
      transformResponse: (response: APIResponse<ChatGroup[]>) => response.data,
    }),
    getGroupMessage: builder.query<
      ExtendedInitalMessage<InitialMessage[]>,
      { groupId: string; filter?: string }
    >({
      query: ({ groupId, filter }) => {
        return `/workspace/${getWorkspace()}/chat/group/${groupId}/message/${filter ?? ""}`;
      },
      providesTags: ["socket-change"],
    }),

    sendGroupMsg: builder.mutation<object, { groupId: string; data: FormData }>(
      {
        query: ({ groupId, data }) => ({
          url: `/workspace/${getWorkspace()}/chat/group/${groupId}/message`,
          method: "POST",
          body: data,
        }),
        invalidatesTags: ["single-group", "single-user"],
      },
    ),

    getSingleGroup: builder.query<ChatGroup, { groupId: string }>({
      query: ({ groupId }) => {
        return `/workspace/${getWorkspace()}/chat/group/${groupId}`;
      },
      providesTags: ["single-group"],
      transformResponse: (response: APIResponse<ChatGroup>) => response.data,
    }),
    mutateGroup: builder.mutation<
      object,
      {
        data?: TCreateChatgroup | TUpdateChatgroup;
        groupId?: string;
        method: REQUEST_METHODS;
      }
    >({
      query: ({ data, groupId, method }) => ({
        url: `/workspace/${getWorkspace()}/chat/group/${groupId ?? ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["group", "single-group", "single-user"],
    }),
    deleteGroup: builder.mutation<
      object,
      {
        data?: TUpdateChatgroup;
        groupId?: string;
        method: REQUEST_METHODS.DELETE;
      }
    >({
      query: ({ data, groupId, method }) => ({
        url: `/workspace/${getWorkspace()}/chat/group/${groupId ?? ""}`,
        method,
        body: data,
      }),
      invalidatesTags: ["group"],
    }),

    // general
    getChatList: builder.query<IChatList, { filter?: string }>({
      query: ({ filter }) => {
        return `/workspace/${getWorkspace()}/chat/list/${filter}`;
      },

      transformResponse: (response: APIResponse<IChatList>) => response?.data,
      providesTags: ["group"],
    }),

    // chatEntitySearch: builder.query<TSearchChatResponse[], string>({
    //   query: (search) => {
    //     return `/workspace/${getWorkspace()}/chat/search?search=${search}`;
    //   },

    //   transformResponse: (response: APIResponse<TSearchChatResponse[]>) =>
    //     response?.data,
    // }),

    mutateMsg: builder.mutation<
      object,
      { messageId: string; data?: TUpdateMessage }
    >({
      query: ({ messageId, data }) => ({
        url: `/workspace/${getWorkspace()}/chat/message/${messageId}`,
        method: data?.message ? REQUEST_METHODS.PUT : REQUEST_METHODS.DELETE,
        body: data,
      }),
      invalidatesTags: ["single-group", "single-user"],
    }),
    seenMutate: builder.mutation<
      object,
      {
        senderId: string;
        entity: ChatType;
        data: {
          date: string;
        };
      }
    >({
      query: ({ senderId, entity, data }) => ({
        url: `/workspace/${getWorkspace()}/chat/${entity.toLowerCase()}/${senderId}/seen`,
        method: REQUEST_METHODS.PATCH,
        body: data,
      }),
    }),

    getSingleMsgEntity: builder.query<Group | User, { id: string }>({
      query: ({ id }) => {
        return `/workspace/${getWorkspace()}/chat/list/${id}`;
      },
      // providesTags: ["group", "single-group"],
      transformResponse: (response: APIResponse<Group | User>) => response.data,
    }),
  }),
});

export const {
  // useLazyChatEntitySearchQuery,
  useGetUserMsgDetailQuery,
  useLazyGetUserMessageQuery,
  useSendUserMsgMutation,
  useMutateUserMsgMutation,
  useGetGroupsQuery,
  useLazyGetGroupMessageQuery,
  useSendGroupMsgMutation,
  useGetSingleGroupQuery,
  useMutateGroupMutation,
  useDeleteGroupMutation,
  useMutateMsgMutation,
  useSeenMutateMutation,
  useGetChatListQuery,
  useLazyGetChatListQuery,
  useLazyGetSingleMsgEntityQuery,
} = messageApi;
